import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { RefreshCw, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";

function formatJson(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
}

function LogDetailPanel({ log }) {
  if (!log) return null;
  
  return (
    <div className="bg-[#FAFAF8] border-t border-[rgba(61,64,53,0.08)] p-4 text-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left column: Source & URLs */}
        <div className="space-y-3">
          <div>
            <div className="font-medium text-[#3D4035] mb-1">Source URL</div>
            <a 
              href={log.source_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#81B29A] hover:underline text-xs break-all flex items-center gap-1"
            >
              {log.source_url || "—"}
              {log.source_url && <ExternalLink size={12} />}
            </a>
          </div>
          
          {log.discovered_urls?.length > 0 && (
            <div>
              <div className="font-medium text-[#3D4035] mb-1">Discovered URLs</div>
              <ul className="list-disc list-inside text-xs text-[#6A6F5C] space-y-0.5">
                {log.discovered_urls.map((url, i) => (
                  <li key={i} className="truncate">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {url.split('/').slice(-2).join('/')}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {(log.menu_valid_from || log.menu_valid_until) && (
            <div>
              <div className="font-medium text-[#3D4035] mb-1">Menu Validity</div>
              <div className="text-xs text-[#6A6F5C]">
                {log.menu_valid_from || "?"} → {log.menu_valid_until || "?"}
              </div>
            </div>
          )}
          
          {log.llm_error && (
            <div>
              <div className="font-medium text-[#D95D39] mb-1">LLM Error</div>
              <div className="text-xs text-[#D95D39] bg-red-50 p-2 rounded">{log.llm_error}</div>
            </div>
          )}
        </div>
        
        {/* Right column: Extracted specials */}
        <div className="space-y-3">
          {log.extracted_specials?.length > 0 && (
            <div>
              <div className="font-medium text-[#3D4035] mb-1">Extracted Specials ({log.extracted_specials.length})</div>
              <div className="max-h-48 overflow-y-auto bg-white border border-[rgba(61,64,53,0.08)] rounded p-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[#6A6F5C]">
                      <th className="pb-1">Name</th>
                      <th className="pb-1">Price</th>
                      <th className="pb-1">Days</th>
                      <th className="pb-1">Cat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {log.extracted_specials.map((sp, i) => (
                      <tr key={i} className="border-t border-[rgba(61,64,53,0.04)]">
                        <td className="py-1 truncate max-w-[140px]" title={sp.name}>{sp.name}</td>
                        <td className="py-1">{sp.price != null ? `${sp.price}€` : "—"}</td>
                        <td className="py-1 text-[#6A6F5C]">{sp.valid_days?.join(", ") || "—"}</td>
                        <td className="py-1 text-[#6A6F5C]">{sp.category || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* LLM provider request/response - full width */}
      {log.llm_calls?.length > 0 ? (
        <div className="mt-4 space-y-3">
          <div className="font-medium text-[#3D4035]">LLM Provider Messages ({log.llm_calls.length})</div>
          {log.llm_calls.map((call, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-medium text-[#6A6F5C] mb-1">Request #{i + 1}</div>
                <pre className="text-xs text-[#6A6F5C] bg-white border border-[rgba(61,64,53,0.08)] rounded p-2 max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
                  {formatJson(call.request)}
                </pre>
              </div>
              <div>
                <div className="text-xs font-medium text-[#6A6F5C] mb-1">Response #{i + 1}</div>
                <pre className="text-xs text-[#6A6F5C] bg-white border border-[rgba(61,64,53,0.08)] rounded p-2 max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
                  {call.response || call.error || "—"}
                </pre>
              </div>
            </div>
          ))}
        </div>
      ) : (log.llm_request || log.llm_response) && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="font-medium text-[#3D4035] mb-1">LLM Request</div>
            <pre className="text-xs text-[#6A6F5C] bg-white border border-[rgba(61,64,53,0.08)] rounded p-2 max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
              {formatJson(log.llm_request)}
            </pre>
          </div>
          <div>
            <div className="font-medium text-[#3D4035] mb-1">LLM Response</div>
            <pre className="text-xs text-[#6A6F5C] bg-white border border-[rgba(61,64,53,0.08)] rounded p-2 max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
              {log.llm_response || "—"}
            </pre>
          </div>
        </div>
      )}

      {/* Input text preview - full width */}
      {log.input_text_preview && (
        <div className="mt-4">
          <div className="font-medium text-[#3D4035] mb-1">Input Text Preview (first 2000 chars)</div>
          <pre className="text-xs text-[#6A6F5C] bg-white border border-[rgba(61,64,53,0.08)] rounded p-2 max-h-32 overflow-y-auto whitespace-pre-wrap break-words">
            {log.input_text_preview}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/logs");
      setLogs(r.data);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="px-4 py-5 sm:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Extraction logs</h1>
          <p className="text-[#6A6F5C] text-sm mt-1">Last 200 extraction runs. Click a row to see details.</p>
        </div>
        <Button variant="outline" onClick={load} className="w-full sm:w-auto" data-testid="logs-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>
      <div className="bg-white border border-[rgba(61,64,53,0.08)] rounded-xl overflow-hidden overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow className="bg-[#F7F5F0]">
              <TableHead className="w-8"></TableHead>
              <TableHead>When</TableHead>
              <TableHead>Restaurant</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Found</TableHead>
              <TableHead>Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#6A6F5C]">Loading…</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#6A6F5C]">No extractions yet.</TableCell></TableRow>
            ) : logs.map((l) => (
              <React.Fragment key={l.id}>
                <TableRow 
                  className="cursor-pointer hover:bg-[#FAFAF8] transition-colors"
                  onClick={() => toggleExpand(l.id)}
                >
                  <TableCell className="w-8">
                    {expandedId === l.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </TableCell>
                  <TableCell className="text-xs">{new Date(l.ran_at).toLocaleString()}</TableCell>
                  <TableCell>{l.restaurant_name}</TableCell>
                  <TableCell>
                    <Badge className={l.status === "ok" ? "bg-[#81B29A]" : "bg-[#D95D39]"}>{l.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">{l.found_count}</TableCell>
                  <TableCell className="max-w-md truncate text-xs text-[#6A6F5C]" title={l.message}>{l.message}</TableCell>
                </TableRow>
                {expandedId === l.id && (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <LogDetailPanel log={l} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
